"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  InvoiceKSeFStatus,
} from "@/components/invoices/invoice-ksef-status";
import { dashboardAPI } from "@/lib/api-client";
import type { DashboardStats, DashboardSummary } from "@/lib/api-client";
import { formatBuyerIdentification } from "@/lib/utils";
import type { Invoice } from "@repo/shared";
import {
  FileText,
  RefreshCw,
  FileEdit,
  Globe,
  Truck,
  Coins,
  FileX,
  Receipt,
  ShoppingCart,
  ReceiptText,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("pl-PL");
}

function formatCurrency(
  amount: number,
  currency?: string,
): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: currency || "PLN",
    currencyDisplay: "narrowSymbol",
  }).format(amount);
}

// ---------------------------------------------------------------------------
// Document type card definitions
// ---------------------------------------------------------------------------

type DocType = "FA" | "FF" | "FK" | "FE" | "WD" | "FW" | "DK" | "SP" | "PA";

interface DocTypeCard {
  type: DocType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
}

const DOC_TYPE_CARDS: DocTypeCard[] = [
  { type: "FA", label: "Invoices (FA)", description: "VAT invoices", icon: FileText, route: "/invoices" },
  { type: "FF", label: "Fiscal (FF)", description: "Fiscal invoices", icon: Receipt, route: "/fiscal-invoices" },
  { type: "FK", label: "Corrections (FK)", description: "Correction invoices", icon: FileEdit, route: "/corrections" },
  { type: "FE", label: "Export (FE)", description: "Export invoices", icon: Globe, route: "/export-invoices" },
  { type: "WD", label: "Intra-EU (WD)", description: "EU delivery invoices", icon: Truck, route: "/wwd" },
  { type: "FW", label: "Currency (FW)", description: "Foreign currency", icon: Coins, route: "/currency-invoices" },
  { type: "DK", label: "Curr. Corr. (DK)", description: "Currency corrections", icon: FileX, route: "/currency-corrections" },
  { type: "SP", label: "Retail Sale (SP)", description: "Daily retail sales", icon: ShoppingCart, route: "/retail-sales" },
  { type: "PA", label: "Receipts (PA)", description: "Receipts (paragony)", icon: ReceiptText, route: "/receipts" },
];

// ---------------------------------------------------------------------------
// Dashboard Page
// ---------------------------------------------------------------------------

export default function DashboardPage() {
  const router = useRouter();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recent, setRecent] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<DocType | null>(null);

  // -----------------------------------------------------------------------
  // Load dashboard data
  // -----------------------------------------------------------------------

  const loadDashboard = useCallback(async (docType?: DocType | null) => {
    try {
      setLoading(true);
      setError(null);
      const data: DashboardSummary = await dashboardAPI.summary(
        docType || undefined,
      );
      setStats(data.stats);
      setRecent(data.recent);
    } catch {
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard(selectedType);
  }, [loadDashboard, selectedType]);

  // -----------------------------------------------------------------------
  // Toggle card selection
  // -----------------------------------------------------------------------

  const handleCardClick = (type: DocType) => {
    setSelectedType((prev) => (prev === type ? null : type));
  };

  const handleRowClick = (invoice: Invoice) => {
    const docType = (invoice as Invoice & { documentType?: string }).documentType;
    switch (docType) {
      case "FK": router.push(`/corrections?id=${invoice.id}`); break;
      case "FE": router.push(`/export-invoices?id=${invoice.id}`); break;
      case "WD": router.push(`/wwd?id=${invoice.id}`); break;
      case "FW": router.push(`/currency-invoices?id=${invoice.id}`); break;
      case "DK": router.push(`/currency-corrections?id=${invoice.id}`); break;
      case "SP": router.push(`/retail-sales?id=${invoice.id}`); break;
      case "PA": router.push(`/receipts?id=${invoice.id}`); break;
      default: router.push(`/invoices?id=${invoice.id}`); break;
    }
  };

  // -----------------------------------------------------------------------
  // Get stat count for a document type
  // -----------------------------------------------------------------------

  const getCount = (type: DocType): number => {
    if (!stats) return 0;
    return stats[type.toLowerCase() as keyof DashboardStats] ?? 0;
  };

  const totalCount = stats
    ? stats.fa + stats.ff + stats.fk + stats.fe + stats.wd + stats.fw + stats.dk
    : 0;

  // -----------------------------------------------------------------------
  // Skeleton loading state
  // -----------------------------------------------------------------------

  if (loading && !stats) {
    return (
      <div className="h-full overflow-y-auto p-6 space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-4 rounded" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-12 mb-1" />
                <Skeleton className="h-3 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            {totalCount} documents across all types.
            {selectedType && (
              <span className="ml-1">
                Filtering by <span className="font-medium">{selectedType}</span>.
              </span>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadDashboard(selectedType)}
          disabled={loading}
        >
          <RefreshCw className={`size-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="py-3 text-sm text-destructive">
            {error}
          </CardContent>
        </Card>
      )}

      {/* Document Type Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {DOC_TYPE_CARDS.map((card) => {
          const isSelected = selectedType === card.type;
          const count = getCount(card.type);
          return (
            <Card
              key={card.type}
              className={`cursor-pointer transition-all hover:border-primary/50 ${
                isSelected
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : ""
              }`}
              onClick={() => handleCardClick(card.type)}
            >
              <CardHeader className="flex flex-row items-start justify-between pb-2 min-h-[52px]">
                <CardTitle className="text-sm font-medium text-muted-foreground leading-tight">
                  {card.label}
                </CardTitle>
                <card.icon className={`size-4 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{count}</div>
                <p className="text-xs text-muted-foreground">{card.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedType
              ? `${DOC_TYPE_CARDS.find((c) => c.type === selectedType)?.label} Documents`
              : "Recent Documents"}
          </CardTitle>
          <CardDescription>
            {selectedType
              ? `Showing the most recent ${recent.length} ${selectedType} documents.`
              : `Showing the most recent ${recent.length} documents across all types.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No documents found.
              {selectedType
                ? ` No ${selectedType} documents in the system yet.`
                : " Documents will appear here once they are imported or created."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead>KSeF Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((invoice) => {
                  const docType = (invoice as Invoice & { documentType?: string }).documentType || "FA";
                  return (
                    <TableRow
                      key={invoice.id}
                      className="cursor-pointer"
                      onClick={() => handleRowClick(invoice)}
                    >
                      <TableCell className="font-medium">
                        {invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {docType}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(invoice.issueDate)}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium text-sm">
                            {invoice.buyer.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatBuyerIdentification(invoice.buyer)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(invoice.totalNet, invoice.currency)}
                        {invoice.plnTotalNet != null && (
                          <div className="text-[10px] text-muted-foreground">
                            {formatCurrency(invoice.plnTotalNet, "PLN")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(invoice.totalGross, invoice.currency)}
                        {invoice.plnTotalGross != null && (
                          <div className="text-[10px] text-muted-foreground font-normal">
                            {formatCurrency(invoice.plnTotalGross, "PLN")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <InvoiceKSeFStatus
                          registration={invoice.kSeFRegistration}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
